import React, { useState } from 'react';
import { Modal, Button, Table, Spinner } from 'react-bootstrap';
import { BsTrash } from 'react-icons/bs';
import { useDispatch } from 'react-redux';
import * as actions from '../../Redux/Actions/index';
import { showErrorToastMessage, showSuccessToastMessage } from '../Toasts/Toasts';

interface IManagerProps {
  show: boolean;
  onHide: () => void;
  data: { _id: string; name: string }[];
  refreshData: () => void;
}

const ActivityTypeManagerModal = ({ show, onHide, data, refreshData }: IManagerProps) => {
  const dispatch = useDispatch();

  // Confirmation State
  const [itemToDelete, setItemToDelete] = useState<{ id: string, name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [blockerMsg, setBlockerMsg] = useState("");
  const initiateDelete = (id: string, name: string) => {
    setItemToDelete({ id, name });
  };

  const confirmDelete = () => {
    if (!itemToDelete) return;
    setIsDeleting(true);

    dispatch(actions.deleteActivityTypeAction(itemToDelete.id, (res: any) => {
      setIsDeleting(false);

      if (res?.success || res?.code === 200 || res?.data?.success) {
        showSuccessToastMessage("Activity Type deleted successfully.");
        refreshData();
        setItemToDelete(null);
      }
      else if (res?.error?.response?.status === 400 || res?.code === 400) {
        setItemToDelete(null);
        const msg = res?.error?.response?.data?.error?.error_description ||
          res?.error?.message ||
          "Cannot delete this Activity Type because it has Tasks assigned.";
        setBlockerMsg(msg);
      }

      // 3. Generic Error Case (Network error, 500, etc.)
      else {
        const errorMsg =
          res?.error?.response?.data?.error?.error_description ||
          res?.error?.message ||
          "Delete failed.";

        showErrorToastMessage(errorMsg); // Keep Toast for generic errors
        setItemToDelete(null);
      }
    }));
  };

  return (
    <>
      {/* Main List */}
      <Modal show={show} onHide={onHide} centered>
        <Modal.Header closeButton><Modal.Title>Manage Activity Types</Modal.Title></Modal.Header>
        <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {data && data.length > 0 ? (
            <Table hover responsive>
              <tbody>
                {data.map((item) => (
                  <tr key={item._id}>
                    <td className="align-middle">{item.name}</td>
                    <td className="text-end">
                      <Button variant="outline-danger" size="sm" onClick={() => initiateDelete(item._id, item.name)}>
                        <BsTrash />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : <p className="text-center text-muted">No Activity Types found.</p>}
        </Modal.Body>
      </Modal>

      {/* Confirmation Modal */}
      <Modal show={!!itemToDelete} onHide={() => setItemToDelete(null)} centered size="sm">
        <Modal.Header closeButton><Modal.Title>Confirm Delete</Modal.Title></Modal.Header>
        <Modal.Body>Are you sure you want to delete <strong>{itemToDelete?.name}</strong>?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setItemToDelete(null)} disabled={isDeleting}>No</Button>
          <Button variant="danger" onClick={confirmDelete} disabled={isDeleting}>
            {isDeleting ? <Spinner size="sm" animation="border" /> : "Yes, Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={!!blockerMsg} onHide={() => setBlockerMsg("")} centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">Cannot Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-4">
          <div style={{ fontSize: "1.1rem", fontWeight: "500" }}>
            {blockerMsg}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setBlockerMsg("")}>
            Okay
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ActivityTypeManagerModal;